import React, { useEffect, useState } from "react";
import type {
  IAttrStatValidate,
  SubmitActionsType,
} from "./types/SubmitActionType";
import { App, Button, Input, Popover, Tooltip } from "antd";
import { CheckOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { AiOutlineClear, AiOutlineEdit } from "react-icons/ai";
import ActionButton from "../../../../masterComponents/actionButton";
import Icon from "../../../../masterComponents/Icon";
import {
  StyledBtnContainer,
  StyledLabel,
  StyledPopoverContainer,
} from "./styles/submitActions.style";
import { useCommonPropertiesContext } from "../../../../../contexts/commonPropertiesContext";
import { useAnalysisAgentContext } from "../../_analysisAgentContext";
import { useFetchData } from "../../../../../hooks/useFetch/post";
import { useFetchData as useClearValidation } from "../../../../../hooks/useFetch/delete";
import { PHRASES_DICTIONARY } from "../../../../../entities/phrasesDictionary";
import { useSingleValueActionsById } from "../../../../../hooks/useEndpoints/useSingleValueActionsById";

const SubmitActions: React.FC<SubmitActionsType> = ({
  hash,
  value,
  setDeferredActionsData,
  fetchAttributeStatistics,
  id,
  state,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpenVerify, setIsOpenVerify] = useState(null);

  const { message: messageApi, modal } = App.useApp();

  const { currentBatch } = useCommonPropertiesContext();
  const { attributeToAnalyze, parentRowSourceValuesParams, activeBatch } =
    useAnalysisAgentContext();

  const workflowState = state ?? parentRowSourceValuesParams.state;

  const curParentBatch =
    parentRowSourceValuesParams?.targetTable === "parent"
      ? currentBatch
      : activeBatch;

  useEffect(() => {
    if (isOpenVerify === null) return;

    if (inputValue !== value) {
      setInputValue(value);
    }
  }, [isOpenVerify]);

  const [
    fetchAttrStatValidate,
    {
      isLoading: isLoadingAttrStatValidate,
      data: attrStatValidateData,
      error: attrStatValidateError,
      clearError: clearAttrStatValidateError,
    },
  ] = useFetchData<IAttrStatValidate>({
    query: "/@batch/statistics/attributes/@sourceAttributeName/validate",
    selectData: (data: IAttrStatValidate) => data,
  });

  useEffect(() => {
    if (attrStatValidateError) {
      messageApi.error(attrStatValidateError.message);
      clearAttrStatValidateError?.();
    }
  }, [attrStatValidateError, messageApi]);

  useEffect(() => {
    if (attrStatValidateData?.result && !isLoadingAttrStatValidate) {
      messageApi.success(
        PHRASES_DICTIONARY.SUCCESS.tags_validated(
          attrStatValidateData?.count ?? 0,
        ),
      );
      fetchAttributeStatistics();
    }
  }, [isLoadingAttrStatValidate, messageApi]);

  const [
    fetchAttrStatDeleteValidation,
    {
      isLoading: isLoadingAttrStatDeleteValidation,
      data: attrStatDeleteValidationData,
      error: attrStatDeleteValidationError,
      clearError: clearAttrStatDeleteValidationError,
    },
  ] = useClearValidation<IAttrStatValidate>({
    query: "/@batch/statistics/attributes/@attributeName/clear-validation",
    selectData: (data: IAttrStatValidate) => data,
  });

  useEffect(() => {
    if (attrStatDeleteValidationError) {
      messageApi.error(attrStatDeleteValidationError.message);
      clearAttrStatDeleteValidationError?.();
    }
  }, [attrStatDeleteValidationError, messageApi]);

  useEffect(() => {
    if (
      attrStatDeleteValidationData?.result &&
      !isLoadingAttrStatDeleteValidation
    ) {
      messageApi.success(
        PHRASES_DICTIONARY.SUCCESS.tags_cleared(
          attrStatDeleteValidationData?.count ?? 0,
        ),
      );
      fetchAttributeStatistics();
    }
  }, [isLoadingAttrStatDeleteValidation, messageApi]);

  const { submitValue, deleteFinalValue, validateFinalValue, clearValidation } =
    useSingleValueActionsById({
      queryParams: { batch: curParentBatch, _id: id },
      onSuccess: ({ action }) => {
        fetchAttributeStatistics();

        if (action === "validateFinalValue") {
          messageApi.success(PHRASES_DICTIONARY.SUCCESS.tags_validated(1));
        }
        if (action === "clearValidation") {
          messageApi.success(PHRASES_DICTIONARY.SUCCESS.tags_cleared(1));
        }
      },
      onError: ({ error }) => {
        messageApi.error(error.message);
      },
    });

  return (
    <>
      <Popover
        placement={"bottomRight"}
        zIndex={900}
        trigger={["click"]}
        content={
          <>
            {workflowState !== "validated" && (
              <StyledPopoverContainer>
                <StyledLabel>Value to verified</StyledLabel>
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </StyledPopoverContainer>
            )}

            <StyledBtnContainer>
              {workflowState === "finals" ? (
                <ActionButton
                  key="validate"
                  text="Validate"
                  icon={<Icon icon={CheckCircleOutlined} />}
                  iconPosition="end"
                  className="primary-btn-color"
                  styles={{ width: 300, gap: "var(--spacing-xs)" }}
                  onClick={() => {
                    if (id) {
                      validateFinalValue({
                        attribute_name: attributeToAnalyze,
                        source_value: value,
                      });
                    } else {
                      fetchAttrStatValidate(
                        {
                          batch: currentBatch,
                          sourceAttributeName: attributeToAnalyze,
                        },
                        {
                          target_attribute_name: attributeToAnalyze,
                          hash: hash,
                          source_value: value,
                        },
                      );
                    }
                    setInputValue(value ?? "");
                    setIsOpenVerify(false);
                  }}
                />
              ) : (
                workflowState === "llm" && (
                  <ActionButton
                    key="submit"
                    text="Verify"
                    icon={<Icon icon={CheckOutlined} />}
                    iconPosition="end"
                    className="primary-btn-color"
                    styles={{ width: 300, gap: "var(--spacing-xs)" }}
                    onClick={() => {
                      if (id) {
                        submitValue({
                          attribute_name: attributeToAnalyze,
                          final_value: inputValue,
                          source_value: value,
                        });
                      } else {
                        setDeferredActionsData((prev) => [
                          ...prev,
                          {
                            hash,
                            type: "SUBMIT",
                            sourceValue: value,
                            targetValue: inputValue,
                          },
                        ]);
                      }

                      setInputValue(value ?? "");
                      setIsOpenVerify(false);
                    }}
                  />
                )
              )}
              <ActionButton
                key="clear"
                text="Clear"
                icon={<Icon icon={AiOutlineClear} />}
                iconPosition="end"
                className="primary-btn-color"
                styles={{ width: 300, gap: "var(--spacing-xs)" }}
                loading={isLoadingAttrStatDeleteValidation}
                onClick={() => {
                  if (workflowState === "finals") {
                    if (id) {
                      deleteFinalValue({
                        source_value: value,
                        attribute_name: attributeToAnalyze,
                      });
                    } else {
                      setDeferredActionsData((prev) => [
                        ...prev,
                        {
                          hash,
                          type: "CLEAR",
                          targetValue: value,
                          sourceValue: value,
                        },
                      ]);
                    }
                    setInputValue(value ?? "");
                    setIsOpenVerify(false);
                  } else if (workflowState === "validated") {
                    if (id) {
                      modal.warning({
                        title: "Warning!",
                        content:
                          PHRASES_DICTIONARY.WARNINGS.warning_clear_validation,
                        onOk: () => {
                          clearValidation({
                            attribute_name: attributeToAnalyze,
                            source_value: value,
                          });

                          setInputValue(value ?? "");
                          setIsOpenVerify(false);
                        },
                        okCancel: true,
                        transitionName: "ant-fade",
                      });
                    } else {
                      modal.warning({
                        title: "Warning!",
                        content:
                          PHRASES_DICTIONARY.WARNINGS.warning_clear_validation,
                        onOk: () => {
                          fetchAttrStatDeleteValidation(
                            {
                              batch: currentBatch,
                              attributeName: attributeToAnalyze,
                            },
                            { hash, source_value: value },
                          );

                          setInputValue(value ?? "");
                          setIsOpenVerify(false);
                        },
                        okCancel: true,
                        transitionName: "ant-fade",
                      });
                    }
                  }
                }}
                disabled={workflowState === "llm"}
              />
            </StyledBtnContainer>
          </>
        }
        open={isOpenVerify}
        onOpenChange={setIsOpenVerify}
      >
        <Tooltip title="Verify" placement="left">
          <Button
            type="primary"
            className="primary-btn-color"
            icon={
              <Icon
                icon={AiOutlineEdit}
                size={20}
                styles={{ display: "flex" }}
              />
            }
          />
        </Tooltip>
      </Popover>
    </>
  );
};

export default SubmitActions;
